"use client";

import {
  SimpleTable,
  SimpleTableBody,
  SimpleTableCell,
  SimpleTableHeader,
  SimpleTableHeaderCell,
  SimpleTableRow
} from "@cmp-src/primitives/SimpleTable/SimpleTable";
import type {
  ReportCatalog,
  ReportParts,
  ReportQuery,
  ReportQueryResult
} from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import React, { useMemo, useState } from "react";
import { formatDelta, formatValue, partsUnit } from "../lib/formatValue";
import { pageWindow } from "../lib/pagination";
import { type PairedResult, rowsByKey, seriesByBucket } from "../lib/pair";

interface ResultTableProps {
  catalog: ReportCatalog;
  result: ReportQueryResult;
  /** The question behind the result, which names the unit of a ratio's parts. */
  query?: ReportQuery;
  /** A second measure's answer, one more column beside the first's. */
  paired?: PairedResult;
  /** When given, clicking a breakdown row pins its members as filters. */
  onDrill?: (dimension: string, memberId: string) => void;
  /** Rows per page; the rest are a page away. A by-person cut can run to thousands. */
  pageSize?: number;
}

const DEFAULT_PAGE_SIZE = 25;

const formatCount = (n: number): string =>
  Number.isInteger(n)
    ? n.toLocaleString("en-US")
    : n.toLocaleString("en-US", { maximumFractionDigits: 1 });

/** "312 / 1,040": the two numbers behind a ratio cell. */
const formatRatioCell = (parts: ReportParts | null): string =>
  parts
    ? `${formatCount(parts.numerator)} / ${formatCount(parts.denominator)}`
    : "—";

type SortColumn =
  | "label"
  | "value"
  | "paired"
  | "parts"
  | "previous"
  | "change";
type Sort = { column: SortColumn; direction: "asc" | "desc" };

type TableRow = {
  key: string;
  drill: (() => void) | undefined;
  cells: { key: string; label: string }[];
  value: number;
  /** The second measure's value, on a paired answer. */
  paired: number | null;
  previous: number | null;
  parts: ReportParts | null;
};

/** What a column sorts by; a missing value sorts last either way. */
const sortKey = (row: TableRow, column: SortColumn): number | string | null => {
  switch (column) {
    case "label":
      return row.cells.map((c) => c.label).join(" ");
    case "value":
      return row.value;
    case "paired":
      return row.paired;
    case "parts":
      return row.parts?.denominator ?? null;
    case "previous":
      return row.previous;
    case "change":
      return row.previous === null ? null : row.value - row.previous;
  }
};

const compare = (
  a: number | string | null,
  b: number | string | null
): number => {
  if (a === null || b === null) {
    return a === b ? 0 : a === null ? 1 : -1;
  }
  if (typeof a === "string" || typeof b === "string") {
    return String(a).localeCompare(String(b), undefined, { numeric: true });
  }
  return a - b;
};

interface HeaderProps {
  column: SortColumn;
  label: string;
  sort: Sort | null;
  onSort: (column: SortColumn) => void;
  isRight?: boolean;
}

const SortableHeader: React.FC<HeaderProps> = ({
  column,
  label,
  sort,
  onSort,
  isRight
}) => {
  const isSorted = sort?.column === column;
  return (
    <SimpleTableHeaderCell
      className={cn(
        "text-[11px]",
        isRight && "text-right",
        isSorted && "text-neutral-900"
      )}
      aria-sort={
        isSorted
          ? sort.direction === "asc"
            ? "ascending"
            : "descending"
          : undefined
      }
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 uppercase hover:text-neutral-900"
      >
        {label}
        <span
          className={cn(
            "text-[9px]",
            isSorted ? "text-neutral-900" : "text-neutral-300"
          )}
        >
          {isSorted && sort.direction === "asc" ? "▲" : "▼"}
        </span>
      </button>
    </SimpleTableHeaderCell>
  );
};

/**
 * Every answer has a table: breakdown cells as rows, or the series buckets
 * when there is no breakdown. Previous-period columns appear with `compare`,
 * a paired measure as a column of its own (and then the previous period
 * stays out); a ratio also shows the numbers it was computed from. Any
 * column sorts on click; numbers start largest first, names A to Z. Long
 * answers come a page at a time.
 */
export const ResultTable: React.FC<ResultTableProps> = ({
  catalog,
  result,
  query,
  paired,
  onDrill,
  pageSize = DEFAULT_PAGE_SIZE
}) => {
  const measure = result.meta.measure;
  const hasPrevious = result.totals.previous !== null && !paired;
  const unit = partsUnit(measure, query);
  // Parts arrive with the API that computes them; until then the column stays out.
  const hasParts = !!unit && !!result.totals.parts;
  const dimensionColumns = result.columns.filter((c) => c.type === "dimension");
  const dimensionLabel = (key: string) =>
    catalog.dimensions.find((d) => d.key === key)?.label ?? key;
  const [sort, setSort] = useState<Sort | null>(null);
  const [page, setPage] = useState(0);
  // A new answer starts from the first page.
  const [seenResult, setSeenResult] = useState(result);
  if (seenResult !== result) {
    setSeenResult(result);
    setPage(0);
  }

  const rows: TableRow[] = useMemo(() => {
    // A count with no matching row is a zero; anything else is unknown.
    const missing = paired?.measure.kind === "count" ? 0 : null;
    const pairedRows = paired ? rowsByKey(paired.result.rows) : null;
    const pairedPoints = paired ? seriesByBucket(paired.result.series) : null;
    const given: TableRow[] =
      dimensionColumns.length > 0
        ? result.rows.map((row) => ({
            key: row.keys.join("|"),
            drill: () => {
              for (const [index, c] of dimensionColumns.entries()) {
                onDrill?.(c.key, row.keys[index]);
              }
            },
            cells: row.labels.map((label, index) => ({
              key: `${dimensionColumns[index]?.key}:${row.keys[index]}`,
              label
            })),
            value: row.value,
            paired: pairedRows
              ? (pairedRows.get(row.keys.join("|"))?.value ?? missing)
              : null,
            previous: row.previous,
            parts: row.parts
          }))
        : result.series.map((point) => ({
            key: point.bucket,
            drill: undefined,
            cells: [{ key: `bucket:${point.bucket}`, label: point.bucket }],
            value: point.value,
            paired: pairedPoints
              ? (pairedPoints.get(point.bucket)?.value ?? missing)
              : null,
            previous: point.previous,
            parts: point.parts
          }));
    if (!sort) {
      return given;
    }
    const sign = sort.direction === "asc" ? 1 : -1;
    return [...given].sort(
      (a, b) => sign * compare(sortKey(a, sort.column), sortKey(b, sort.column))
    );
  }, [result, paired, dimensionColumns, onDrill, sort]);

  const toggleSort = (column: SortColumn) => {
    setSort((prev) =>
      prev?.column === column
        ? { column, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { column, direction: column === "label" ? "asc" : "desc" }
    );
    setPage(0);
  };

  const firstColumns =
    dimensionColumns.length > 0
      ? dimensionColumns.map((c) => dimensionLabel(c.key))
      : [result.meta.grain];
  const paging = pageWindow(rows.length, page, pageSize);
  const shown = rows.slice(paging.from, paging.to);

  return (
    <div className="flex flex-col gap-1">
      <SimpleTable wrapperClassName="overflow-hidden rounded-lg bg-[#F0F4F7]">
        <SimpleTableHeader>
          <SimpleTableRow>
            {firstColumns.map((label, index) =>
              index === 0 ? (
                <SortableHeader
                  key={label}
                  column="label"
                  label={label}
                  sort={sort}
                  onSort={toggleSort}
                />
              ) : (
                <SimpleTableHeaderCell key={label} className="text-[11px]">
                  {label}
                </SimpleTableHeaderCell>
              )
            )}
            <SortableHeader
              column="value"
              label={measure.label}
              sort={sort}
              onSort={toggleSort}
              isRight
            />
            {paired ? (
              <SortableHeader
                column="paired"
                label={paired.measure.label}
                sort={sort}
                onSort={toggleSort}
                isRight
              />
            ) : null}
            {hasParts ? (
              <SortableHeader
                column="parts"
                label={unit ?? ""}
                sort={sort}
                onSort={toggleSort}
                isRight
              />
            ) : null}
            {hasPrevious ? (
              <>
                <SortableHeader
                  column="previous"
                  label="Previous"
                  sort={sort}
                  onSort={toggleSort}
                  isRight
                />
                <SortableHeader
                  column="change"
                  label="Change"
                  sort={sort}
                  onSort={toggleSort}
                  isRight
                />
              </>
            ) : null}
          </SimpleTableRow>
        </SimpleTableHeader>
        <SimpleTableBody>
          {shown.map((row) => {
            const delta = formatDelta(measure, row.value, row.previous);
            const isDrillable = !!onDrill && !!row.drill;
            return (
              <SimpleTableRow
                key={row.key}
                onClick={row.drill}
                className={
                  isDrillable ? "cursor-pointer hover:bg-neutral-50" : undefined
                }
                title={isDrillable ? "Click to drill into this row" : undefined}
              >
                {row.cells.map((cell) => (
                  <SimpleTableCell
                    key={cell.key}
                    className="text-[13px] text-neutral-700"
                  >
                    {cell.label}
                  </SimpleTableCell>
                ))}
                <SimpleTableCell className="text-right font-semibold text-[13px] text-neutral-900 tabular-nums">
                  {formatValue(measure, row.value)}
                </SimpleTableCell>
                {paired ? (
                  <SimpleTableCell className="text-right text-[13px] text-neutral-700 tabular-nums">
                    {row.paired === null
                      ? "—"
                      : formatValue(measure, row.paired)}
                  </SimpleTableCell>
                ) : null}
                {hasParts ? (
                  <SimpleTableCell className="whitespace-nowrap text-right text-[12.5px] text-neutral-500 tabular-nums">
                    {formatRatioCell(row.parts)}
                  </SimpleTableCell>
                ) : null}
                {hasPrevious ? (
                  <>
                    <SimpleTableCell className="text-right text-[13px] text-neutral-500 tabular-nums">
                      {row.previous === null
                        ? "—"
                        : formatValue(measure, row.previous)}
                    </SimpleTableCell>
                    <SimpleTableCell className="text-right text-[13px] tabular-nums">
                      {delta ? (
                        <span
                          className={
                            delta.isFlat
                              ? "text-neutral-400"
                              : delta.isGood
                                ? "text-[#006300]"
                                : "text-[#a52a2a]"
                          }
                        >
                          {delta.text}
                        </span>
                      ) : (
                        "—"
                      )}
                    </SimpleTableCell>
                  </>
                ) : null}
              </SimpleTableRow>
            );
          })}
          {rows.length === 0 ? (
            <SimpleTableRow>
              <SimpleTableCell className="text-[13px] text-neutral-400">
                Nothing in this period.
              </SimpleTableCell>
            </SimpleTableRow>
          ) : null}
        </SimpleTableBody>
      </SimpleTable>
      {paging.pages > 1 ? (
        <div className="flex items-center justify-between px-1 text-[11.5px] text-neutral-500">
          <span className="tabular-nums">
            {`${(paging.from + 1).toLocaleString("en-US")}–${paging.to.toLocaleString("en-US")} of ${rows.length.toLocaleString("en-US")}`}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={paging.current === 0}
              onClick={() => setPage(paging.current - 1)}
              className="rounded-md px-2 py-0.5 hover:bg-neutral-100 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={paging.current >= paging.pages - 1}
              onClick={() => setPage(paging.current + 1)}
              className="rounded-md px-2 py-0.5 hover:bg-neutral-100 disabled:opacity-40 disabled:hover:bg-transparent"
            >
              Next
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
